/* Scroll choreography: native scrolling, reversible progress, no runtime dependency. */
(() => {
  const controllers = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => { const t = clamp(value); return t * t * (3 - 2 * t); };

  function init(root) {
    if (controllers.has(root)) return;
    const controller = new AbortController();
    controllers.set(root, controller);
    const { signal } = controller;
    const header = document.querySelector('.site-header');
    const sections = [...root.querySelectorAll('section[id]')];
    const nav = [...document.querySelectorAll('.desktop-nav a, .mobile-menu nav a')];
    const records = [];
    const add = (selector, kind, stagger = 0, scope = root) => {
      [...scope.querySelectorAll(selector)].forEach((element, index) => {
        if (records.some(record => record.element === element)) return;
        records.push({ element, kind, stagger: index % 5 * stagger, top: 0, height: 0, entry: 1, exit: 0, drift: 0, last: '' });
      });
    };
    add('.section-heading > .eyebrow, .contact > .eyebrow', 'label');
    add('.section-heading h2', 'heading');
    add('.section-heading > div > p', 'copy', 0);
    add('.hero-copy > *', 'hero', .02);
    add('.brand-world', 'card', .045);
    add('.capability-flow > li', 'card', .035);
    add('.production-copy > *, .technology-copy > *', 'copy', .025);
    add('.stitch-workstation, .software-stack', 'spatial');
    add('.network-map', 'network');
    add('.network-detail', 'copy');
    add('.principle-list > article', 'row', .025);
    add('.contact-layout > div:first-child > *', 'copy', .025);
    add('.contact-form', 'form');
    const footer = document.querySelector('.site-footer');
    if (footer) add('.footer-directory > *, .footer-legal', 'copy', .025, footer);

    let frame = 0, measured = false, needsMeasure = true, previousTime = 0;
    let sectionBounds = [], documentHeight = 1, viewport = innerHeight, compact = innerWidth < 768;
    const stopped = () => reduced.matches || document.documentElement.classList.contains('ip-motion-paused');
    // Layout coordinates stay independent of the visual transforms we apply.
    const layoutTop = element => {
      let top = 0;
      for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
      return top;
    };
    const measure = () => {
      viewport = innerHeight; compact = innerWidth < 768;
      records.forEach(record => { record.top = layoutTop(record.element); record.height = record.element.offsetHeight; });
      sectionBounds = sections.map(section => ({ id: section.id, top: layoutTop(section) }));
      documentHeight = Math.max(1, document.documentElement.scrollHeight - viewport);
      needsMeasure = false;
    };
    const request = () => { if (!frame && !document.hidden && !signal.aborted) frame = requestAnimationFrame(render); };
    const render = time => {
      frame = 0;
      if (signal.aborted || document.hidden) return;
      if (needsMeasure) measure();
      const scroll = window.scrollY;
      const disabled = stopped();
      const delta = previousTime ? Math.min(50, time - previousTime) : 16.7;
      const blend = 1 - Math.pow(.76, delta / 16.7);
      previousTime = time;
      let settling = false;
      records.forEach(record => {
        const { element, kind, height } = record;
        const top = record.top - scroll, bottom = top + height;
        const focused = element.matches(':focus-within');
        const entryRange = viewport * (compact ? .2 : .28);
        const offset = compact ? 0 : record.stagger * viewport;
        const targetEntry = disabled || focused ? 1 : ease((viewport * .97 - top - offset) / entryRange);
        // Fully readable through the centre; reverse softly at either viewport edge.
        const targetExit = disabled || focused || kind === 'form' ? 0 : 1 - ease(bottom / (viewport * .15));
        const targetDrift = disabled || focused ? 0 : Math.max(-1, Math.min(1, (top + height / 2 - viewport / 2) / viewport));
        const near = top < viewport * 1.2 && bottom > -viewport * .2;
        const snap = !measured || disabled || focused || !near;
        record.entry = snap ? targetEntry : record.entry + (targetEntry - record.entry) * blend;
        record.exit = snap ? targetExit : record.exit + (targetExit - record.exit) * blend;
        record.drift = snap ? targetDrift : record.drift + (targetDrift - record.drift) * blend;
        if (!snap && Math.max(Math.abs(record.entry - targetEntry), Math.abs(record.exit - targetExit), Math.abs(record.drift - targetDrift)) > .001) settling = true;
        const reveal = record.entry * (1 - record.exit);
        const spatial = kind === 'spatial' || kind === 'card';
        const travel = (kind === 'heading' ? 42 : spatial ? 70 : 30) * (compact ? .45 : 1);
        const depth = kind === 'spatial' ? 25 : kind === 'network' ? 16 : 0;
        const y = disabled || focused ? 0 : (1 - record.entry) * travel - record.exit * travel * .45 + record.drift * depth * (compact ? .35 : 1);
        const scale = disabled || focused || kind === 'network' ? 1 : 1 - (1 - reveal) * (spatial ? .035 : .006);
        const opacity = disabled || focused ? 1 : .18 + reveal * .82;
        const tilt = disabled || focused || compact || !spatial ? 0 : (1 - record.entry) * 2.2 - record.exit * 1.2;
        const values = `${y.toFixed(2)},${scale.toFixed(4)},${opacity.toFixed(3)},${tilt.toFixed(2)}`;
        if (values !== record.last) {
          element.style.setProperty('--scroll-y', `${y.toFixed(2)}px`);
          element.style.setProperty('--scroll-scale', scale.toFixed(4));
          element.style.setProperty('--scroll-opacity', opacity.toFixed(3));
          element.style.setProperty('--scroll-tilt', `${tilt.toFixed(2)}deg`);
          record.last = values;
        }
        element.classList.toggle('is-scroll-near', near && !disabled);
      });
      header?.style.setProperty('--reading-progress', clamp(scroll / documentHeight).toFixed(4));
      let active = '';
      sectionBounds.forEach(section => { if (scroll + viewport * .38 >= section.top) active = section.id; });
      nav.forEach(link => {
        const selected = link.hash === `#${active}`;
        if (selected) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (!measured) {
        records.forEach(({ element, kind }) => { element.dataset.scrollMotion = kind; });
        root.classList.add('has-scroll-motion');
        measured = true;
      }
      if (settling) request();
    };
    window.addEventListener('scroll', request, { passive: true, signal });
    window.addEventListener('resize', () => { needsMeasure = true; request(); }, { passive: true, signal });
    reduced.addEventListener('change', request, { signal });
    document.addEventListener('click', event => { if (event.target.closest('[data-motion-toggle]')) request(); }, { signal });
    document.addEventListener('focusin', request, { signal });
    document.addEventListener('focusout', request, { signal });
    document.addEventListener('visibilitychange', () => { previousTime = 0; if (document.hidden) { cancelAnimationFrame(frame); frame = 0; } else request(); }, { signal });
    let resizeObserver;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => { needsMeasure = true; request(); });
      [root, ...sections, ...(footer ? [footer] : [])].forEach(element => resizeObserver.observe(element));
    }
    signal.addEventListener('abort', () => {
      cancelAnimationFrame(frame); resizeObserver?.disconnect();
      records.forEach(({ element }) => {
        delete element.dataset.scrollMotion;
        element.classList.remove('is-scroll-near');
        ['--scroll-y', '--scroll-scale', '--scroll-opacity', '--scroll-tilt'].forEach(property => element.style.removeProperty(property));
      });
      root.classList.remove('has-scroll-motion');
      header?.style.removeProperty('--reading-progress');
      nav.forEach(link => link.removeAttribute('aria-current'));
      controllers.delete(root);
    });
    request();
  }
  document.querySelectorAll('[data-system]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-system]').forEach(init));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-system]').forEach(root => controllers.get(root)?.abort()));
})();
